import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion"; // eslint-disable-line no-unused-vars
import CreateClientModal from "../../../components/agent/clients/CreateClientModal";
import ModernClientNotesModal from "../../../components/agent/ModernClientNotesModal";
import DeleteConfirmationModal from "../../../components/agent/DeleteConfirmationModal";
import { useUser } from "../../../context/UserContext";
import { canAccessAdmin } from "../../../utils/roleUtils";
import {
  Search,
  UserPlus,
  Users,
  Mail,
  Phone,
  Eye,
  Trash2,
  Edit3,
  Save,
  X,
  FileText,
  MapPin,
  Filter,
  Download,
  MoreVertical,
  Calendar,
  DollarSign,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Target,
  ChevronDown,
  ChevronUp,
  SortAsc,
  SortDesc,
  RefreshCw,
  Sparkles,
  Building2,
  Heart,
  Zap,
  List,
  CheckSquare,
  Square,
  Check,
  ArrowLeft,
  Plus,
  MessageSquare,
  TrendingUp,
  Loader2,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import "../../../components/agent/crm/CRMDashboard.css";
import toast from "../../../utils/toast";

export default function UnifiedClientManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // For client details view
  const { user } = useUser();

  // State Management
  const [clients, setClients] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalClients: 0,
    totalTrips: 0,
    avgTrips: 0,
    activeClients: 0,
    newThisMonth: 0,
    totalValue: 0,
    avgValue: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filters, setFilters] = useState({
    status: "all",
    source: "all",
    lastContact: "all",
    value: "all",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    relationship_status: "",
  });
  const [saving, setSaving] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedClientForNotes, setSelectedClientForNotes] = useState(null);
  const [clientTripsCount, setClientTripsCount] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showClientTypeDropdown, setShowClientTypeDropdown] = useState(false);

  // New state for different views
  const [currentView, setCurrentView] = useState("list"); // 'list', 'add', 'details'
  const [selectedClientDetails, setSelectedClientDetails] = useState(null);
  const [clientTrips, setClientTrips] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Add Client form state
  const [addClientForm, setAddClientForm] = useState({
    fullname: "",
    email: "",
    phone: "",
    relationship_status: "lead",
    source: "website",
    communication_preference: "email",
    preferred_destination: "disney_world",
  });
  const [addClientErrors, setAddClientErrors] = useState({});
  const [savingClient, setSavingClient] = useState(false);

  // ========================================
  // FUNCTION DEFINITIONS (must come before useEffects)
  // ========================================

  // Fetch trips count for each client
  const fetchTripsCount = useCallback(async (clientsList) => {
    try {
      const tripsCountPromises = clientsList.map(async (client) => {
        try {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/api/trips?clientId=${
              client.clientid
            }`,
            {
              credentials: "include",
            }
          );
          if (res.ok) {
            const trips = await res.json();
            return { clientId: client.clientid, count: trips.length };
          }
        } catch (error) {
          console.error(
            `Error fetching trips for client ${client.clientid}:`,
            error
          );
        }
        return { clientId: client.clientid, count: 0 };
      });

      const tripsCounts = await Promise.all(tripsCountPromises);
      const tripsCountMap = {};
      tripsCounts.forEach(({ clientId, count }) => {
        tripsCountMap[clientId] = count;
      });
      setClientTripsCount(tripsCountMap);
    } catch (error) {
      console.error("Error fetching trips counts:", error);
    }
  }, []);

  // Fetch client details for the details view
  const fetchClientDetails = useCallback(
    async (clientId) => {
      try {
        setLoadingDetails(true);

        // Fetch client details
        const clientResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/api/clients/${clientId}`,
          {
            credentials: "include",
          }
        );

        if (!clientResponse.ok) {
          throw new Error("Client not found");
        }

        const clientData = await clientResponse.json();
        setSelectedClientDetails(clientData);

        // Fetch client's trips
        const tripsResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/api/clients/${clientId}/trips`,
          {
            credentials: "include",
          }
        );

        if (tripsResponse.ok) {
          const tripsData = await tripsResponse.json();
          setClientTrips(tripsData);
        }
      } catch (error) {
        console.error("Error fetching client data:", error);
        toast.error("Failed to load client details");
        navigate("/agent/crm/clients");
      } finally {
        setLoadingDetails(false);
      }
    },
    [navigate]
  );

  // ========================================
  // USE EFFECTS
  // ========================================

  // Determine current view based on URL
  useEffect(() => {
    if (location.pathname === "/agent/crm/clients/new") {
      setCurrentView("add");
    } else if (location.pathname.startsWith("/agent/crm/clients/") && id) {
      setCurrentView("details");
      // fetchClientDetails will be called separately to avoid dependency issues
    } else {
      setCurrentView("list");
    }
  }, [location.pathname, id]);

  // Fetch client details when viewing details
  useEffect(() => {
    if (currentView === "details" && id) {
      fetchClientDetails(id);
    }
  }, [currentView, id, fetchClientDetails]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Fetch clients function (extracted for reuse)
  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/clients`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch clients");
      const data = await res.json();
      setClients(data);

      // Fetch trips count for each client
      fetchTripsCount(data);
    } catch (err) {
      console.error("Error loading clients:", err);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [fetchTripsCount]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/clients/analytics`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch analytics");
        const data = await res.json();
        setAnalytics(data);
      } catch (err) {
        console.error("Error loading analytics:", err);
      }
    };

    fetchClients();
    fetchAnalytics();
  }, [fetchClients]);

  // Listen for client creation events from Sidebar
  useEffect(() => {
    const handleClientCreated = (event) => {
      const newClient = event.detail;
      // Refresh the client list
      fetchClients();
      // Also update analytics
      const fetchAnalytics = async () => {
        try {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/api/clients/analytics`,
            {
              credentials: "include",
            }
          );
          if (res.ok) {
            const data = await res.json();
            setAnalytics(data);
          }
        } catch (err) {
          console.error("Error refreshing analytics:", err);
        }
      };
      fetchAnalytics();
    };

    window.addEventListener('clientCreated', handleClientCreated);
    return () => {
      window.removeEventListener('clientCreated', handleClientCreated);
    };
  }, [fetchClients]);

  const handleClientCreated = (newClient) => {
    setClients([...clients, newClient]);

    // Refresh analytics
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/clients/analytics`,
          {
            credentials: "include",
          }
        );
        if (res.ok) {
          const data = await res.json();
          setAnalytics(data);
        }
      } catch (err) {
        console.error("Error refreshing analytics:", err);
      }
    };
    fetchAnalytics();
  };

  // Helper function to get client initials
  const getClientInitials = (fullName) => {
    if (!fullName) return "C";
    const names = fullName.trim().split(" ");
    if (names.length >= 2) {
      return (
        names[0].charAt(0) + names[names.length - 1].charAt(0)
      ).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
  };

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone; // Return original if not 10 digits
  };

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = clients.filter((client) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        (client.fullname || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (client.email || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (client.phone || "").includes(searchQuery);

      // Status filter
      const matchesStatus =
        filters.status === "all" ||
        client.relationship_status === filters.status;

      // Source filter
      const matchesSource =
        filters.source === "all" ||
        (client.source || "unknown") === filters.source;

      // Last contact filter
      const matchesLastContact =
        filters.lastContact === "all" ||
        (filters.lastContact === "recent" &&
          new Date(client.createdat || Date.now()) >
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

      return (
        matchesSearch && matchesStatus && matchesSource && matchesLastContact
      );
    });

    // Sort clients
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = (a.fullname || "").toLowerCase();
          bValue = (b.fullname || "").toLowerCase();
          break;
        case "value":
          aValue = clientTripsCount[a.clientid] || 0;
          bValue = clientTripsCount[b.clientid] || 0;
          break;
        case "lastContact":
          aValue = new Date(a.createdat || 0);
          bValue = new Date(b.createdat || 0);
          break;
        case "trips":
          aValue = clientTripsCount[a.clientid] || 0;
          bValue = clientTripsCount[b.clientid] || 0;
          break;
        default:
          aValue = (a.fullname || "").toLowerCase();
          bValue = (b.fullname || "").toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [clients, searchQuery, filters, sortBy, sortOrder, clientTripsCount]);

  // Handle bulk operations
  const handleSelectAll = () => {
    if (selectedClients.length === filteredAndSortedClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(
        filteredAndSortedClients.map((client) => client.clientid)
      );
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} on clients:`, selectedClients);
    // Implement bulk actions here
  };

  // Handle edit mode
  const handleEditClick = () => {
    setEditFormData({
      fullname: selectedClient.fullname || "",
      email: selectedClient.email || "",
      phone: selectedClient.phone || "",
      relationship_status: selectedClient.relationship_status || "lead",
    });
    setIsEditing(true);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!selectedClient) return;

    setSaving(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/clients/${
          selectedClient.clientid
        }`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editFormData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update client");
      }

      // Update the client in the local state
      setClients(
        clients.map((client) =>
          client.clientid === selectedClient.clientid
            ? { ...client, ...editFormData }
            : client
        )
      );

      // Update the selected client
      setSelectedClient({ ...selectedClient, ...editFormData });

      setIsEditing(false);
      toast.success("Client updated successfully!");
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error(error.message || "Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      fullname: "",
      email: "",
      phone: "",
      relationship_status: "lead",
    });
  };

  // Handle opening notes modal
  const handleOpenNotes = (client) => {
    setSelectedClientForNotes(client);
    setShowNotesModal(true);
  };

  // Handle closing notes modal
  const handleCloseNotes = () => {
    setShowNotesModal(false);
    setSelectedClientForNotes(null);
  };

  const openDeleteModal = (client, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/clients/${
          clientToDelete.clientid
        }`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Unknown error" }));
        throw new Error(errorData.message || "Failed to delete client");
      }

      setClients(
        clients.filter((client) => client.clientid !== clientToDelete.clientid)
      );
      toast.success("Client deleted successfully!");
      setShowDeleteModal(false);
      setClientToDelete(null);
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error(error.message || "Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  // Get status color and label
  const getStatusInfo = (status) => {
    switch (status) {
      case "customer":
        return { color: "success", label: "Customer" };
      case "lead":
        return { color: "warning", label: "Lead" };
      case "prospect":
        return { color: "info", label: "Prospect" };
      case "inactive":
        return { color: "error", label: "Inactive" };
      default:
        return { color: "gray", label: "Unknown" };
    }
  };

  // Client type options for custom dropdown
  const clientTypeOptions = [
    { value: "lead", label: "Lead", icon: Target, color: "text-yellow-600" },
    {
      value: "prospect",
      label: "Prospect",
      icon: Star,
      color: "text-blue-600",
    },
    {
      value: "customer",
      label: "Customer",
      icon: CheckCircle,
      color: "text-green-600",
    },
    {
      value: "vip",
      label: "VIP Client",
      icon: Heart,
      color: "text-purple-600",
    },
  ];

  const getSelectedClientType = () => {
    return (
      clientTypeOptions.find(
        (option) => option.value === editFormData.relationship_status
      ) || clientTypeOptions[0]
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
              <div className="flex gap-2 items-center">
                <div className="w-6 h-6 rounded-full border-2 border-black animate-spin border-t-transparent"></div>
                <span className="text-sm text-gray-600">
                  Loading clients...
                </span>
              </div>
            </div>
    );
  }

  // New functions for Add Client view
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setAddClientForm({ ...addClientForm, phone: formatted });
  };

  const validateAddClientForm = () => {
    const newErrors = {};

    if (!addClientForm.fullname?.trim()) {
      newErrors.fullname = "Full name is required";
    }

    if (!addClientForm.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addClientForm.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (addClientForm.phone && addClientForm.phone.length < 14) {
      newErrors.phone = "Please enter a complete phone number";
    }

    setAddClientErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddClientSubmit = async (e) => {
    e.preventDefault();

    if (!validateAddClientForm()) {
      return;
    }

    setSavingClient(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/crm/clients`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            ...addClientForm,
            created_by: user?.userid,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create client");
      }

      const result = await response.json();

      if (result.success) {
        toast.success("Client created successfully!");
        navigate("/agent/crm/clients");
      } else {
        throw new Error(result.message || "Failed to create client");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error(error.message || "Failed to create client");
    } finally {
      setSavingClient(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "lead":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "prospect":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "customer":
        return "bg-green-100 text-green-800 border-green-200";
      case "vip":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "lead":
        return <AlertCircle size={12} />;
      case "prospect":
        return <Clock size={12} />;
      case "customer":
        return <CheckCircle size={12} />;
      case "vip":
        return <CheckCircle size={12} />;
      default:
        return <AlertCircle size={12} />;
    }
  };

  const destinationOptions = [
    { value: "disney_world", label: "Walt Disney World", icon: "🏰" },
    { value: "disneyland", label: "Disneyland Resort", icon: "✨" },
    { value: "disney_cruise", label: "Disney Cruise Line", icon: "🚢" },
    { value: "universal_orlando", label: "Universal Orlando", icon: "🌍" },
  ];

  // Add Client View Component
  function AddClientView() {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex gap-4 items-center mb-8">
          <button
            onClick={() => navigate("/agent/crm/clients")}
            className="flex gap-2 items-center px-3 py-2 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
            Back to Clients
          </button>
        </div>

        <div className="flex gap-3 items-center mb-6">
          <div className="p-3 bg-blue-100 rounded-lg">
            <UserPlus size={24} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add New Client</h1>
            <p className="text-gray-600">
              Create a new client profile in your CRM system
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm">
          <form onSubmit={handleAddClientSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={addClientForm.fullname}
                  onChange={(e) =>
                    setAddClientForm({
                      ...addClientForm,
                      fullname: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    addClientErrors.fullname
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter client's full name"
                />
                {addClientErrors.fullname && (
                  <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                    <AlertCircle size={16} />
                    {addClientErrors.fullname}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={addClientForm.email}
                  onChange={(e) =>
                    setAddClientForm({
                      ...addClientForm,
                      email: e.target.value,
                    })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    addClientErrors.email
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter client's email"
                />
                {addClientErrors.email && (
                  <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                    <AlertCircle size={16} />
                    {addClientErrors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={addClientForm.phone}
                  onChange={handlePhoneChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    addClientErrors.phone
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="(555) 123-4567"
                />
                {addClientErrors.phone && (
                  <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                    <AlertCircle size={16} />
                    {addClientErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Relationship Status
                </label>
                <select
                  value={addClientForm.relationship_status}
                  onChange={(e) =>
                    setAddClientForm({
                      ...addClientForm,
                      relationship_status: e.target.value,
                    })
                  }
                  className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="lead">Lead</option>
                  <option value="prospect">Prospect</option>
                  <option value="customer">Customer</option>
                  <option value="vip">VIP Client</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Source
                </label>
                <select
                  value={addClientForm.source}
                  onChange={(e) =>
                    setAddClientForm({
                      ...addClientForm,
                      source: e.target.value,
                    })
                  }
                  className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="google_ads">Google Ads</option>
                  <option value="facebook_ads">Facebook Ads</option>
                  <option value="email_campaign">Email Campaign</option>
                  <option value="phone_call">Phone Call</option>
                  <option value="walk_in">Walk-in</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Communication Preference
                </label>
                <select
                  value={addClientForm.communication_preference}
                  onChange={(e) =>
                    setAddClientForm({
                      ...addClientForm,
                      communication_preference: e.target.value,
                    })
                  }
                  className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="text">Text Message</option>
                  <option value="any">Any Method</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Preferred Destination
              </label>
              <select
                value={addClientForm.preferred_destination}
                onChange={(e) =>
                  setAddClientForm({
                    ...addClientForm,
                    preferred_destination: e.target.value,
                  })
                }
                className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {destinationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 justify-end pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate("/agent/crm/clients")}
                className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingClient}
                className="flex gap-2 items-center px-6 py-3 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingClient ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Creating Client...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Create Client
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    );
  }

  // Client Details View Component
  function ClientDetailsView() {
    if (loadingDetails) {
      return (
        <div className="flex justify-center items-center min-h-96">
          <div className="flex gap-2 items-center">
            <Loader2 size={24} className="text-blue-600 animate-spin" />
            <span className="text-gray-600">Loading client details...</span>
          </div>
        </div>
      );
    }

    if (!selectedClientDetails) {
      return (
        <div className="flex flex-col justify-center items-center text-center min-h-96">
          <AlertCircle size={48} className="mb-4 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            Client Not Found
          </h2>
          <p className="mb-6 text-gray-600">
            The client you're looking for doesn't exist or has been deleted.
          </p>
          <button
            onClick={() => navigate("/agent/crm/clients")}
            className="px-6 py-3 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Back to Clients
          </button>
        </div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex gap-4 items-center mb-8">
          <button
            onClick={() => navigate("/agent/crm/clients")}
            className="flex gap-2 items-center px-3 py-2 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
            Back to Clients
          </button>
        </div>

        {/* Client Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex gap-4 items-center">
            <div className="flex justify-center items-center w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-700 rounded-xl">
              <span className="text-2xl font-bold text-white">
                {getClientInitials(selectedClientDetails.fullname)}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {selectedClientDetails.fullname}
              </h1>
              <div className="flex gap-3 items-center mt-2">
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(
                    selectedClientDetails.relationship_status
                  )}`}
                >
                  {getStatusIcon(selectedClientDetails.relationship_status)}
                  {selectedClientDetails.relationship_status || "lead"}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-600">
                  {clientTrips.length} trips
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Client Information */}
          <div className="space-y-6 lg:col-span-2">
            {/* Basic Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h2 className="flex gap-2 items-center mb-4 text-xl font-semibold text-gray-900">
                <Users size={20} />
                Basic Information
              </h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <p className="text-gray-900">
                    {selectedClientDetails.fullname || "Not provided"}
                  </p>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="flex gap-2 items-center">
                    <Mail size={16} className="text-gray-400" />
                    <span className="text-gray-900">
                      {selectedClientDetails.email || "Not provided"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <div className="flex gap-2 items-center">
                    <Phone size={16} className="text-gray-400" />
                    <span className="text-gray-900">
                      {selectedClientDetails.phone || "Not provided"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(
                      selectedClientDetails.relationship_status
                    )}`}
                  >
                    {getStatusIcon(selectedClientDetails.relationship_status)}
                    {selectedClientDetails.relationship_status || "lead"}
                  </span>
                </div>
              </div>
            </div>

            {/* Client's Trips */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="flex gap-2 items-center text-xl font-semibold text-gray-900">
                  <Calendar size={20} />
                  Client's Trips ({clientTrips.length})
                </h2>
                <button
                  onClick={() =>
                    navigate(`/agent/crm/trips/new?clientId=${id}`)
                  }
                  className="flex gap-2 items-center px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
                >
                  <Plus size={18} />
                  Create Trip
                </button>
              </div>

              {clientTrips.length === 0 ? (
                <div className="py-8 text-center">
                  <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">
                    No trips created yet for this client.
                  </p>
                  <button
                    onClick={() =>
                      navigate(`/agent/crm/trips/new?clientId=${id}`)
                    }
                    className="px-4 py-2 mt-4 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
                  >
                    Create First Trip
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {clientTrips.map((trip) => (
                    <div
                      key={trip.tripid}
                      className="flex justify-between items-center p-4 rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
                    >
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {trip.trip_name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {trip.destination} • {trip.start_date} -{" "}
                          {trip.end_date}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          navigate(`/agent/crm/trips/${trip.tripid}`)
                        }
                        className="flex gap-1 items-center px-3 py-1 text-sm text-blue-600 transition-colors hover:text-blue-800"
                      >
                        <Eye size={16} />
                        View
                      </button>
                      {/* Note: This navigates to CRMPlaceholder (admin tool), not itinerary view, so pixie dust is intentionally bypassed */}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Quick Stats
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">Total Trips</span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {clientTrips.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <DollarSign size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Lifetime Value
                    </span>
                  </div>
                  <span className="font-semibold text-gray-900">$0</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <TrendingUp size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Avg Trip Value
                    </span>
                  </div>
                  <span className="font-semibold text-gray-900">$0</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() =>
                    navigate(`/agent/crm/trips/new?clientId=${id}`)
                  }
                  className="flex gap-2 items-center px-4 py-3 w-full text-left text-gray-700 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <Plus size={18} />
                  Create New Trip
                </button>
                <button
                  onClick={() =>
                    navigate(`/agent/crm/communications?clientId=${id}`)
                  }
                  className="flex gap-2 items-center px-4 py-3 w-full text-left text-gray-700 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <MessageSquare size={18} />
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Client List View Component (existing functionality)
  function ClientListView() {
    return (
      <div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="crm-main-content"
        >
          {/* Header */}
          <div className="flex flex-col gap-4 justify-between mb-4 md:flex-row md:items-center animate-fade-in-up">
            <div>
              <h1 className="text-xl font-bold tracking-tight leading-tight text-black sm:text-2xl lg:text-3xl text-balance">
                Client Management
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your clients and their contact information
              </p>
            </div>
            <button
              onClick={() => setShowAddClientModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-400 shadow sm:px-5 sm:py-2.5"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Add New Client</span>
              <span className="sm:hidden">Add Client</span>
            </button>
          </div>

          {/* Analytics Cards - Black/White Style */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 gap-3 mb-4 sm:grid-cols-2 lg:grid-cols-4 animate-fade-in-up sm:gap-4"
          >
            <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium text-gray-600 sm:text-sm">
                    Total Clients
                  </p>
                  <p className="text-xl font-bold text-black sm:text-2xl">
                    {analytics.totalClients}
                  </p>
                </div>
                <Users className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
              </div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium text-gray-600 sm:text-sm">
                    Active Clients
                  </p>
                  <p className="text-xl font-bold text-black sm:text-2xl">
                    {analytics.activeClients || 0}
                  </p>
                </div>
                <CheckCircle className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
              </div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium text-gray-600 sm:text-sm">
                    Total Trips
                  </p>
                  <p className="text-xl font-bold text-black sm:text-2xl">
                    {analytics.totalTrips}
                  </p>
                </div>
                <Calendar className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
              </div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium text-gray-600 sm:text-sm">
                    Avg Trips/Client
                  </p>
                  <p className="text-xl font-bold text-black sm:text-2xl">
                    {analytics.avgTrips}
                  </p>
                </div>
                <Users className="w-6 h-6 text-gray-700 sm:w-8 sm:h-8" />
              </div>
            </div>
          </motion.div>

          {/* Controls */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1 sm:flex-none">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="py-2 pr-3 pl-10 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                type="button"
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  showFilters
                    ? "text-blue-600 bg-blue-50 border border-blue-300"
                    : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
                {showFilters ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            <div className="text-xs text-gray-600 sm:text-sm">
              {filteredAndSortedClients.length} of {clients.length} clients
            </div>
          </motion.div>

          {/* Advanced Filters - aligned with other agent pages like ModernSalesPipeline */}
          {showFilters && (
            <motion.div
              variants={itemVariants}
              className="p-4 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Client Type */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Client Type
                  </label>
                  <div className="relative">
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="py-2 pr-8 pl-3 w-full text-sm bg-white rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Types</option>
                      <option value="lead">Lead</option>
                      <option value="prospect">Prospect</option>
                      <option value="customer">Customer</option>
                    </select>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Source */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Source
                  </label>
                  <div className="relative">
                    <select
                      value={filters.source}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          source: e.target.value,
                        }))
                      }
                      className="py-2 pr-8 pl-3 w-full text-sm bg-white rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Sources</option>
                      <option value="website">Website</option>
                      <option value="referral">Referral</option>
                      <option value="social">Social Media</option>
                      <option value="advertising">Advertising</option>
                    </select>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Last Contact */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Contact
                  </label>
                  <div className="relative">
                    <select
                      value={filters.lastContact}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          lastContact: e.target.value,
                        }))
                      }
                      className="py-2 pr-8 pl-3 w-full text-sm bg-white rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Time</option>
                      <option value="recent">Last 7 Days</option>
                      <option value="month">This Month</option>
                      <option value="quarter">This Quarter</option>
                    </select>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Sort By
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="py-2 pr-8 pl-3 w-full text-sm bg-white rounded-lg border border-gray-300 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="name">Name</option>
                        <option value="value">Total Value</option>
                        <option value="lastContact">Last Contact</option>
                        <option value="trips">Number of Trips</option>
                      </select>
                      <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="p-2 text-gray-600 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                      onClick={() =>
                        setSortOrder((prev) =>
                          prev === "asc" ? "desc" : "asc"
                        )
                      }
                    >
                      {sortOrder === "asc" ? (
                        <SortAsc className="w-4 h-4" />
                      ) : (
                        <SortDesc className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-4">
                <button
                  type="button"
                  onClick={() =>
                    setFilters({
                      status: "all",
                      source: "all",
                      lastContact: "all",
                      value: "all",
                    })
                  }
                  className="text-xs font-medium text-gray-600 hover:text-gray-900 sm:text-sm"
                >
                  Clear filters
                </button>
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="inline-flex items-center px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
                >
                  Done
                </button>
              </div>
            </motion.div>
          )}

          {/* Bulk Actions */}
          {selectedClients.length > 0 && (
            <motion.div
              variants={itemVariants}
              className="p-3 mb-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-800">
                  {selectedClients.length} client
                  {selectedClients.length !== 1 ? "s" : ""} selected
                </span>
                <div className="flex gap-2">
                  <button
                    className="flex gap-1 items-center px-3 py-1 text-sm font-medium text-blue-600 bg-white rounded-md border border-blue-300 transition-colors hover:bg-blue-50"
                    onClick={() => handleBulkAction("email")}
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                  <button
                    className="flex gap-1 items-center px-3 py-1 text-sm font-medium text-blue-600 bg-white rounded-md border border-blue-300 transition-colors hover:bg-blue-50"
                    onClick={() => handleBulkAction("export")}
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Clients List */}
          {filteredAndSortedClients.length === 0 ? (
            <motion.div
              variants={itemVariants}
              className="flex flex-col justify-center items-center px-6 py-12 w-full text-center bg-white rounded-xl border border-gray-200 shadow animate-fade-in-up"
            >
              <Users className="mb-4 w-12 h-12 text-gray-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-800 sm:text-xl">
                No Clients Found
              </h2>
              <p className="mb-6 max-w-md text-sm text-gray-600">
                {searchQuery || Object.values(filters).some((f) => f !== "all")
                  ? "Try adjusting your search criteria or filters."
                  : "You haven't added any clients yet. Click 'Add New Client' to get started."}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowAddClientModal(true)}
                  className="inline-block px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow transition hover:bg-blue-700 sm:px-5"
                >
                  Add New Client
                </button>
              )}
            </motion.div>
          ) : (
            <motion.div
              variants={itemVariants}
              className="space-y-2 animate-fade-in-up"
            >
              {/* Select All Header */}
              <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <button
                  className="flex gap-2 items-center text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
                  onClick={handleSelectAll}
                >
                  {selectedClients.length ===
                  filteredAndSortedClients.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  <span>Select All</span>
                </button>
              </div>

              {filteredAndSortedClients.map((client) => {
                const statusInfo = getStatusInfo(client.relationship_status);
                const isSelected = selectedClients.includes(client.clientid);

                return (
                  <motion.div
                    key={client.clientid}
                    className={`flex flex-col gap-3 p-3 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 sm:flex-row sm:justify-between sm:items-center sm:p-4 ${
                      isSelected ? "bg-blue-50 ring-2 ring-blue-500" : ""}`}
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {/* Selection Checkbox */}
                    <div className="flex gap-3 items-center">
                      <button
                        className="p-1 text-gray-400 transition-colors hover:text-gray-600"
                        onClick={() => handleSelectClient(client.clientid)}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>

                      {/* Client Info */}
                      <div className="flex flex-1 items-center space-x-3 sm:space-x-4">
                        <div className="flex-shrink-0">
                          <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-700 rounded-lg sm:w-12 sm:h-12">
                            <span className="text-base font-semibold text-white sm:text-lg">
                              {getClientInitials(client.fullname)}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex gap-2 items-center mb-1">
                            <h3 className="text-base font-semibold text-black truncate sm:text-lg">
                              {client.fullname}
                            </h3>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                statusInfo.color === "success"
                                  ? "bg-green-100 text-green-800"
                                  : statusInfo.color === "warning"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : statusInfo.color === "info"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1 mt-1 text-xs text-gray-500 sm:flex-row sm:items-center sm:space-x-4 sm:text-sm">
                            <div className="flex items-center space-x-1">
                              <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="truncate">{client.email}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>{formatPhoneNumber(client.phone)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>
                                {clientTripsCount[client.clientid] || 0} trip
                                {(clientTripsCount[client.clientid] || 0) !== 1
                                  ? "s"
                                  : ""}
                              </span>
                            </div>
                            {canAccessAdmin(user?.role) && client.agent_firstname && (
                              <div className="flex items-center space-x-1">
                                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="truncate">
                                  Agent: {client.agent_firstname} {client.agent_lastname}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end items-center space-x-2">
                      <button
                        onClick={() => handleOpenNotes(client)}
                        className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors sm:px-3 sm:py-1.5 sm:text-sm"
                        title="View and edit client notes"
                      >
                        <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Client Notes</span>
                      </button>
                      <button
                        onClick={() =>
                          navigate("/agent/crm/clients/" + client.clientid)
                        }
                        className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-black border border-gray-400 rounded-md hover:bg-gray-100 transition-colors sm:px-3 sm:py-1.5 sm:text-sm"
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => openDeleteModal(client, e)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors sm:p-1.5"
                        title="Delete client"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-[1400px] mx-auto">
        {currentView === "add" && <AddClientView />}
        {currentView === "details" && <ClientDetailsView />}
        {currentView === "list" && <ClientListView />}
      </div>

      {/* Enhanced Client Details Modal */}
      {selectedClient && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-4 w-full max-w-2xl bg-white rounded-lg shadow-xl animate-fade-in-up max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-700 rounded-lg">
                  <span className="text-sm font-bold text-white">
                    {getClientInitials(selectedClient.fullname)}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {isEditing ? "Edit Client Details" : "Client Details"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {isEditing
                      ? "Update client information"
                      : "View and manage client information"}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 items-center">
                {!isEditing && (
                  <button
                    onClick={handleEditClick}
                    className="flex gap-1 items-center px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md transition-colors hover:bg-gray-200"
                  >
                    <Edit3 size={16} />
                    Edit
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedClient(null);
                    handleCancelEdit();
                    setShowClientTypeDropdown(false);
                  }}
                  className="p-1.5 text-gray-400 rounded-md transition-colors hover:text-gray-600 hover:bg-gray-100"
                >
                  <span className="sr-only">Close</span>✕
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Basic Information */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="flex gap-2 items-center mb-3 text-base font-semibold text-gray-900">
                  <Users size={16} />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.fullname}
                        onChange={(e) =>
                          handleInputChange("fullname", e.target.value)
                        }
                        className="px-3 py-2 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="py-2 text-gray-900">
                        {selectedClient.fullname}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className="px-3 py-2 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="py-2 text-gray-900">
                        {selectedClient.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editFormData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        className="px-3 py-2 w-full rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    ) : (
                      <p className="py-2 text-gray-900">
                        {formatPhoneNumber(selectedClient.phone) ||
                          "Not provided"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Client Type
                    </label>
                    {isEditing ? (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() =>
                            setShowClientTypeDropdown(!showClientTypeDropdown)
                          }
                          className="flex justify-between items-center px-3 py-2 w-full text-left bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400"
                        >
                          <div className="flex gap-2 items-center">
                            {React.createElement(getSelectedClientType().icon, {
                              className: `w-4 h-4 ${
                                getSelectedClientType().color
                              }`,
                            })}
                            <span className="text-sm text-gray-900">
                              {getSelectedClientType().label}
                            </span>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform ${
                              showClientTypeDropdown ? "rotate-180" : ""}`}
                          />
                        </button>

                        <AnimatePresence>
                          {showClientTypeDropdown && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="overflow-hidden absolute z-50 mt-1 w-full bg-white rounded-lg border border-gray-300 shadow-lg"
                            >
                              {clientTypeOptions.map((option) => {
                                const OptionIcon = option.icon;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                      handleInputChange(
                                        "relationship_status",
                                        option.value
                                      );
                                      setShowClientTypeDropdown(false);
                                    }}
                                    className={`w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                                      editFormData.relationship_status ===
                                      option.value
                                        ? "bg-blue-50"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex gap-2 items-center">
                                      <OptionIcon
                                        className={`w-4 h-4 ${option.color}`}
                                      />
                                      <span className="text-sm font-medium text-gray-900">
                                        {option.label}
                                      </span>
                                    </div>
                                    {editFormData.relationship_status ===
                                      option.value && (
                                      <Check className="w-4 h-4 text-blue-600" />
                                    )}
                                  </button>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center py-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full border ${
                            getStatusInfo(selectedClient.relationship_status)
                              .color === "success"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : getStatusInfo(
                                  selectedClient.relationship_status
                                ).color === "warning"
                              ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                              : getStatusInfo(
                                  selectedClient.relationship_status
                                ).color === "info"
                              ? "bg-blue-100 text-blue-800 border-blue-200"
                              : "bg-gray-100 text-gray-800 border-gray-200"
                          }`}
                        >
                          {
                            getStatusInfo(selectedClient.relationship_status)
                              .label
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Trip Information */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="flex gap-2 items-center mb-3 text-base font-semibold text-gray-900">
                  <Calendar size={16} />
                  Trip Information
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-600">
                      Total Trips
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {clientTripsCount[selectedClient.clientid] || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-600">
                      Client Since
                    </p>
                    <p className="text-xs text-gray-900">
                      {selectedClient.createdat
                        ? new Date(
                            selectedClient.createdat
                          ).toLocaleDateString()
                        : "Unknown"}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-600">Status</p>
                    <p className="text-xs text-gray-900">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          getStatusInfo(selectedClient.relationship_status)
                            .color === "success"
                            ? "bg-green-100 text-green-800"
                            : getStatusInfo(selectedClient.relationship_status)
                                .color === "warning"
                            ? "bg-yellow-100 text-yellow-800"
                            : getStatusInfo(selectedClient.relationship_status)
                                .color === "info"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {
                          getStatusInfo(selectedClient.relationship_status)
                            .label
                        }
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div>
                  <button
                    onClick={() => handleOpenNotes(selectedClient)}
                    className="flex gap-1 items-center px-3 py-1.5 text-sm text-blue-700 bg-blue-100 rounded-md transition-colors hover:bg-blue-200"
                  >
                    <FileText className="w-4 h-4" />
                    Client Notes
                  </button>
                </div>

                {isEditing && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        handleCancelEdit();
                        setShowClientTypeDropdown(false);
                      }}
                      className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      disabled={saving}
                      className="flex gap-1 items-center px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        <Save size={16} />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      <CreateClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onClientCreated={handleClientCreated}
      />

      {/* Client Notes Modal */}
      {selectedClientForNotes && (
        <ModernClientNotesModal
          clientId={selectedClientForNotes.clientid}
          isOpen={showNotesModal}
          onClose={handleCloseNotes}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setClientToDelete(null);
        }}
        onConfirm={() => handleDeleteClient()}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        itemName={clientToDelete ? clientToDelete.fullname : ""}
        itemType="client"
        isLoading={deleting}
        confirmText="Delete Client"
        cancelText="Cancel"
      />
    </>
  );
}